import React from 'react';
import { X, Calendar, Users, Car, MapPin, Globe, MessageSquare } from 'lucide-react';

interface QuickQuoteDetailProps {
  quote: any;
  onClose: () => void;
}

const QuickQuoteDetail: React.FC<QuickQuoteDetailProps> = ({ quote, onClose }) => {
  const { tripName, quickQuote, quoteRequest } = quote;
  
  // Use either quickQuote or quoteRequest (for backward compatibility)
  const quoteData = quickQuote || quoteRequest;
  
  if (!quoteData) {
    return null;
  }

  const { serviceType, travelStatus, dates, travelers, preferences, driverOnlyDetails, communicationMethod, status } = quoteData;
  const isDriverOnly = serviceType === 'driver_only';

  const formatDate = (dateStr: string) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString();
  };

  const getStatusBadge = () => {
    const statusColors = {
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusColors[status as keyof typeof statusColors] || statusColors.pending}`}>
        {status || 'pending'}
      </span>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-br from-purple-500 to-blue-500 text-white p-6 rounded-t-2xl z-10">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-2xl font-bold mb-2">Quick Quote Request</h2>
              <p className="text-purple-100">{tripName}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 transition-all"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          <div className="mt-4">
            {getStatusBadge()}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Service Type */}
          <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
            <h3 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
              <span className="text-purple-500">{isDriverOnly ? '🚗' : '🗺️'}</span>
              Service Type
            </h3>
            <p className="text-gray-700">
              {isDriverOnly ? 'Driver Only Service' : 'Full Trip Planning'}
            </p>
          </div>

          {/* Travel Status & Dates */}
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-purple-500" />
              Travel Details
            </h3>
            <div className="space-y-2">
              <p className="text-sm">
                <span className="font-semibold text-gray-700">Status:</span>{' '}
                <span className="text-gray-600">
                  {travelStatus === 'visiting_soon' ? 'Visiting Soon' : 'Already in Sri Lanka'}
                </span>
              </p>
              {dates?.arrivalDate && (
                <p className="text-sm">
                  <span className="font-semibold text-gray-700">Dates:</span>{' '}
                  <span className="text-gray-600">
                    {formatDate(dates.arrivalDate)} - {formatDate(dates.departureDate)}
                  </span>
                </p>
              )}
              {dates?.arrivalTime && (
                <p className="text-sm">
                  <span className="font-semibold text-gray-700">Times:</span>{' '}
                  <span className="text-gray-600">
                    {dates.arrivalTime} - {dates.departureTime}
                  </span>
                </p>
              )}
            </div>
          </div>

          {/* Travelers */}
          {travelers && (
            <div className="bg-white rounded-xl p-4 border border-gray-200">
              <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                <Users className="w-5 h-5 text-purple-500" />
                Travelers
              </h3>
              <p className="text-gray-700">
                {travelers.adults} Adults, {travelers.children} Children, {travelers.infants} Infants
              </p>
            </div>
          )}

          {/* Driver Only Details */}
          {isDriverOnly && driverOnlyDetails && (
            <>
              <div className="bg-white rounded-xl p-4 border border-gray-200">
                <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <Car className="w-5 h-5 text-purple-500" />
                  Vehicle Type
                </h3>
                <p className="text-gray-700 capitalize">{driverOnlyDetails.vehicleType?.replace(/_/g, ' ')}</p>
              </div>

              {driverOnlyDetails.roughItinerary && (
                <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
                  <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-purple-500" />
                    Rough Itinerary
                  </h3>
                  <p className="text-gray-700 whitespace-pre-wrap">{driverOnlyDetails.roughItinerary}</p>
                </div>
              )}

              {driverOnlyDetails.specialNeeds?.hasSpecialNeeds && (
                <div className="bg-white rounded-xl p-4 border border-gray-200">
                  <h3 className="font-bold text-gray-800 mb-2">Special Needs</h3>
                  <p className="text-gray-700">{driverOnlyDetails.specialNeeds.details}</p>
                </div>
              )}
            </>
          )}

          {/* Full Trip Details */}
          {!isDriverOnly && preferences && (
            <>
              {preferences.placesToVisit && preferences.placesToVisit.length > 0 && (
                <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
                  <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-purple-500" />
                    Places to Visit
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {preferences.placesToVisit.map((place: string) => (
                      <span key={place} className="px-3 py-1 bg-purple-200 text-purple-800 rounded-full text-sm">
                        {place}
                      </span>
                    ))}
                  </div>
                  {preferences.otherPlaces && (
                    <p className="text-sm text-gray-600 mt-2">
                      <span className="font-semibold">Other:</span> {preferences.otherPlaces}
                    </p>
                  )}
                </div>
              )}

              {preferences.activitiesToDo && preferences.activitiesToDo.length > 0 && (
                <div className="bg-white rounded-xl p-4 border border-gray-200">
                  <h3 className="font-bold text-gray-800 mb-3">Activities</h3>
                  <div className="flex flex-wrap gap-2">
                    {preferences.activitiesToDo.map((activity: string) => (
                      <span key={activity} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                        {activity.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </span>
                    ))}
                  </div>
                  {preferences.otherActivities && (
                    <p className="text-sm text-gray-600 mt-2">
                      <span className="font-semibold">Other:</span> {preferences.otherActivities}
                    </p>
                  )}
                </div>
              )}

              {preferences.travelStyle && (
                <div className="bg-white rounded-xl p-4 border border-gray-200">
                  <h3 className="font-bold text-gray-800 mb-2">Preferences</h3>
                  <p className="text-sm">
                    <span className="font-semibold text-gray-700">Travel Style:</span>{' '}
                    <span className="text-gray-600 capitalize">{preferences.travelStyle?.replace(/_/g, ' ')}</span>
                  </p>
                  <p className="text-sm">
                    <span className="font-semibold text-gray-700">Budget Style:</span>{' '}
                    <span className="text-gray-600 capitalize">{preferences.budgetStyle?.replace(/_/g, ' ')}</span>
                  </p>
                </div>
              )}
            </>
          )}

          {/* Language & Communication */}
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
              <Globe className="w-5 h-5 text-purple-500" />
              Language Preference
            </h3>
            <p className="text-gray-700">
              {(isDriverOnly ? driverOnlyDetails?.languagePreference : preferences?.languagePreference) || 'English'}
            </p>
          </div>

          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-purple-500" />
              Preferred Contact Method
            </h3>
            <p className="text-gray-700 uppercase">{communicationMethod}</p>
          </div>

          {/* Additional Notes */}
          {(preferences?.additionalNotes || driverOnlyDetails?.additionalNotes) && (
            <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
              <h3 className="font-bold text-gray-800 mb-2">Additional Notes</h3>
              <p className="text-gray-700">
                {preferences?.additionalNotes || driverOnlyDetails?.additionalNotes}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 p-4 rounded-b-2xl border-t">
          <button
            onClick={onClose}
            className="w-full px-6 py-3 bg-gradient-to-br from-purple-500 to-blue-500 text-white rounded-xl font-semibold hover:from-purple-600 hover:to-blue-600 transition-all"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuickQuoteDetail;

